import { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Check, Filter } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const FIELD_ORDER = [
  ["incident_date", "Date"], ["incident_time", "Time"],
  ["site", "Site"], ["location", "Location"],
  ["incident_type", "Type"], ["severity", "Severity"],
  ["description", "Description"],
  ["people_involved", "People involved"], ["witnesses", "Witnesses"],
  ["actions_taken", "Actions taken"], ["follow_up_required", "Follow-up required"],
  ["estimated_cost_nzd", "Estimated cost (NZD)"],
  ["police_notified", "Police notified"], ["insurance_claim", "Insurance claim"],
  ["manager_signature", "Manager signature"],
];

const STATUS_COLOR = {
  DRAFT: "bg-gray-100 text-gray-700",
  SUBMITTED: "bg-amber-100 text-amber-800",
  ACKNOWLEDGED: "bg-emerald-100 text-emerald-800",
};

export default function AdminSiteIncidents() {
  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("SUBMITTED");
  const [active, setActive] = useState(null);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = statusFilter && statusFilter !== "ALL" ? { status: statusFilter } : {};
      const { data } = await api.get("/admin/site-incidents", { params });
      setRows(data.incidents || []);
    } catch (e) { toast.error(e?.response?.data?.detail || "Load failed"); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const acknowledge = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/site-incidents/${active.id}/acknowledge`, { notes });
      toast.success("Acknowledged · manager notified");
      setActive(null); setNotes(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Acknowledge failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 max-w-5xl mx-auto space-y-4">
      <div className="flex items-center gap-2">
        <AlertTriangle className="h-5 w-5 text-amber-600" />
        <h1 className="text-xl font-bold text-[#1A2744]">Site incident reports</h1>
      </div>
      <p className="text-xs text-gray-600">
        Manager-submitted reports of site-level incidents (equipment, property damage, theft,
        safety). Acknowledge once reviewed; the submitting manager is notified.
      </p>

      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-gray-500" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px] h-9" data-testid="site-incidents-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All statuses</SelectItem>
            <SelectItem value="SUBMITTED">Submitted</SelectItem>
            <SelectItem value="ACKNOWLEDGED">Acknowledged</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card><CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Manager</TableHead><TableHead>Date</TableHead>
            <TableHead>Type</TableHead><TableHead>Site</TableHead>
            <TableHead>Severity</TableHead><TableHead>Submitted</TableHead>
            <TableHead>Status</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-gray-500 py-6">No site incidents</TableCell></TableRow>}
            {rows.map((r) => (
              <TableRow key={r.id} data-testid={`site-incident-row-${r.id}`}>
                <TableCell className="text-sm">{r.manager_name}</TableCell>
                <TableCell className="text-xs">{r.form?.incident_date || "—"}</TableCell>
                <TableCell className="text-xs">{(r.form?.incident_type || "—").replace(/_/g, " ")}</TableCell>
                <TableCell className="text-xs">{r.site || "—"}</TableCell>
                <TableCell className="text-xs">{r.form?.severity || "—"}</TableCell>
                <TableCell className="text-xs text-gray-500">{r.submitted_at?.slice(0, 16).replace("T", " ") || "—"}</TableCell>
                <TableCell><span className={`px-2 py-0.5 rounded text-[10px] font-mono ${STATUS_COLOR[r.status] || "bg-gray-100"}`}>{r.status}</span></TableCell>
                <TableCell><Button size="sm" variant="outline" onClick={() => { setActive(r); setNotes(""); }} data-testid={`site-incident-view-${r.id}`}>View</Button></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent></Card>

      {active && (
        <Dialog open={true} onOpenChange={(v) => { if (!v) { setActive(null); setNotes(""); } }}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Site incident · {active.manager_name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              {FIELD_ORDER.map(([k, label]) => {
                const v = active.form?.[k];
                if (v === undefined || v === null || v === "" || v === false) return null;
                return (
                  <div key={k} className="border-b border-gray-100 pb-1">
                    <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">{label}</div>
                    <div className="text-sm break-words">{typeof v === "boolean" ? (v ? "Yes" : "No") : String(v)}</div>
                  </div>
                );
              })}
              {active.acknowledged_at && (
                <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-2 text-xs">
                  Acknowledged by {active.acknowledged_by_name} on {active.acknowledged_at?.slice(0, 16).replace("T", " ")}
                  {active.admin_notes && <div className="mt-1"><strong>Notes:</strong> {active.admin_notes}</div>}
                </div>
              )}
            </div>
            {active.status === "SUBMITTED" && (
              <>
                <Textarea rows={3} placeholder="Admin notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="site-incident-ack-notes" />
                <DialogFooter>
                  <Button variant="outline" onClick={() => { setActive(null); setNotes(""); }}>Close</Button>
                  <Button onClick={acknowledge} disabled={busy} className="bg-emerald-600 text-white" data-testid="site-incident-ack-confirm">
                    <Check className="h-3.5 w-3.5 mr-1" /> Acknowledge
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
